import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, fireStore } from "../firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Default admin account. Keep in sync with isAdmin() in firestore.rules.
export const DEFAULT_ADMIN_EMAIL = "admin@dobedosoft.com";

// Mirrors the security rules: a user is an admin when /admins/{uid} or
// /admins/{email} exists, or they sign in with the default admin email.
// Anything looser here would show a dashboard the rules then refuse to load.
export async function checkIsAdmin(user) {
  if (!user) return false;
  const email = (user.email || "").trim().toLowerCase();
  if (email === DEFAULT_ADMIN_EMAIL) return true;

  try {
    if ((await getDoc(doc(fireStore, "admins", user.uid))).exists()) return true;
    if (email && (await getDoc(doc(fireStore, "admins", email))).exists()) return true;
  } catch (error) {
    console.warn("Error verifying admin status in Firestore:", error);
  }
  return false;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  async function login(email, password) {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    if (!(await checkIsAdmin(credential.user))) {
      await signOut(auth);
      throw new Error("This account does not have admin access.");
    }
    // Set state now so the redirect after login doesn't race the auth listener.
    setIsAdmin(true);
    setCurrentUser(credential.user);
    return credential;
  }

  function logout() {
    return signOut(auth);
  }

  // Firebase does not reveal whether an account exists, so neither do we.
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email.trim());
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAdmin(await checkIsAdmin(user));
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAdmin,
    loading,
    login,
    logout,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Public pages render immediately; only admin routes wait for auth (see ProtectedRoute). */}
      {children}
    </AuthContext.Provider>
  );
}

